import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';

/**
 * Levélnapló. (3.0 / 4.)
 *
 * Minden kiküldött levél egy sor: mikor, kinek, mivel, sikerült-e. A
 * sikertelennél ott a hiba szövege is — abból derül ki, hogy rossz cím
 * volt-e, vagy a kimenő szerver nem engedett be.
 */

interface NaploSor {
  id: number;
  idopont: string;
  tipus: string;
  cimzett: string;
  targy: string;
  sikeres: boolean;
  hiba: string | null;
  teszt_mod: boolean;
  fiok: string | null;
  ki: string | null;
}

const TIPUS_NEV: Record<string, string> = {
  szallitolevel: 'Szállítólevél',
  kiadas: 'Átadás',
  szamla: 'Számla',
  felszolitas: 'Felszólítás',
  szerzodes: 'Szerződés',
};

export function Levelnaplo({ onHiba }: { onHiba: (h: string) => void }) {
  const [sorok, setSorok] = useState<NaploSor[]>([]);
  const [csakHiba, setCsakHiba] = useState(false);

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<{ sorok: NaploSor[] }>(
        csakHiba ? '/api/level/naplo?csak_sikertelen=1' : '/api/level/naplo',
      );
      setSorok(v.sorok);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A levélnaplót nem sikerült betölteni.');
    }
  }, [csakHiba, onHiba]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const sikertelen = sorok.filter((s) => !s.sikeres).length;

  return (
    <>
      <div className="actbar" style={{ marginTop: 12 }}>
        <button className={`sb${csakHiba ? ' on' : ''}`} onClick={() => setCsakHiba((v) => !v)}>
          Csak a sikertelenek
        </button>
        <button className="btn sm" onClick={() => void betolt()}>
          Frissítés
        </button>
        <span className="hint">
          {sorok.length} levél
          {sikertelen > 0 ? ` · ebből ${sikertelen} nem ment ki` : ''}
        </span>
      </div>

      {sorok.length === 0 ? (
        <div className="blank" style={{ marginTop: 16 }}>
          {csakHiba ? 'Nincs sikertelen küldés.' : 'Még nem ment ki levél.'}
        </div>
      ) : (
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Időpont</th>
              <th>Mit</th>
              <th>Kinek</th>
              <th>Tárgy</th>
              <th>Fiók</th>
              <th>Ki küldte</th>
              <th>Állapot</th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.id}>
                <td className="mono">{s.idopont.slice(0, 16).replace('T', ' ')}</td>
                <td>{TIPUS_NEV[s.tipus] ?? s.tipus}</td>
                <td>{s.cimzett}</td>
                <td>
                  {s.targy}
                  {s.hiba && <small className="bad">{s.hiba}</small>}
                </td>
                <td>{s.fiok ?? '—'}</td>
                <td>{s.ki ?? '—'}</td>
                <td>
                  <span className={s.sikeres ? 'jelzo ok' : 'jelzo bad'}>
                    {s.sikeres ? (s.teszt_mod ? 'teszt' : 'kiment') : 'nem ment ki'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
